/**
 * Chart theming.
 *
 * The goal is a terminal that reads like a trading venue rather than a widget
 * dropped onto a marketplace: dark by default, almost no chrome, grid lines
 * present but barely, and the price the loudest thing on screen.
 *
 * Up and down colours are read from the site's own semantic ink tokens rather
 * than hardcoded, so the chart's green and red are the same green and red as
 * every other gain and loss on the page. The alkanes chart learned this the
 * hard way — there were two greens and two reds before it started reading
 * tokens — and the same lesson applies here.
 */

/**
 * Resolve a CSS custom property to a concrete `rgb()` string.
 *
 * The tokens are stored as bare HSL components (`142 72% 29%`), which is the
 * Tailwind convention. That composes into CSS Color 4 space-separated syntax,
 * which browsers accept but some chart parsers do not, so the value is
 * resolved through the browser into `rgb()` before it is handed over.
 */
export function tokenColor(name: string, fallback: string, from?: Element | null): string {
	if (typeof window === 'undefined' || typeof document === 'undefined') {
		return fallback
	}
	try {
		// Read from the chart's own element, NOT the root.
		//
		// The /alkanes palette is scoped to `.alkanes-surface`, which has no
		// light variant and defines its own background, accent and inks. Reading
		// the root there returns the site theme's values instead — which is how
		// the alkanes chart ended up pure black with the wrong accent while the
		// surface around it was #060510 with a purple cast.
		const source = from ?? document.documentElement
		const raw = getComputedStyle(source).getPropertyValue(name).trim()
		if (!raw) {
			return fallback
		}
		// Already a concrete colour (hex or rgb) — pass it straight through.
		if (/^(#|rgb|hsl\()/.test(raw)) {
			return raw
		}
		const probe = document.createElement('span')
		probe.style.color = `hsl(${raw.replace(/\s+/g, ', ')})`
		probe.style.display = 'none'
		document.body.appendChild(probe)
		const resolved = getComputedStyle(probe).color
		probe.remove()
		return /^rgba?\(/.test(resolved) ? resolved : fallback
	} catch {
		return fallback
	}
}

/**
 * The background the chart is actually sitting on.
 *
 * Walks up from the container until it finds an element that paints a
 * background — that element is what the chart will appear to sit on, so that
 * is the colour it should be.
 *
 * It deliberately does NOT read the `--background` token. That token is the
 * page CANVAS, and a chart is almost never dropped straight onto the canvas:
 * on /alkanes it lives inside a `bg-card` panel at #11131d while the canvas
 * behind it is #060510. Preferring the token painted the chart the page colour
 * and left it reading as a darker hole punched through its own card.
 *
 * Walking also means this needs no knowledge of which surfaces exist. A card,
 * a modal, a raised panel or the canvas itself all resolve correctly, and a new
 * surface works without being taught about it.
 */
export function surfaceColor(from: Element | null | undefined, fallback: string): string {
	if (typeof window === 'undefined' || !from) {
		return fallback
	}
	try {
		let el: Element | null = from
		while (el) {
			const bg = getComputedStyle(el).backgroundColor
			// Skip the transparents: wrappers that paint nothing are not the
			// surface, they are just layout between the chart and it.
			if (bg && bg !== 'transparent' && !/^rgba\(\s*0,\s*0,\s*0,\s*0\s*\)$/.test(bg)) {
				return bg
			}
			el = el.parentElement
		}
	} catch {
		// fall through
	}
	return fallback
}

/** Optional stylesheet injected into the chart's iframe. */
export const TV_CSS_URL = '/charting_library/ow-theme.css'

export function LOADING_SCREEN(dark: boolean, from?: Element | null) {
	return {
		// The spinner sits on the surface too — a grey flash before the chart
		// paints is the same mismatch, just briefer.
		backgroundColor: surfaceColor(from, dark ? '#0a0a0a' : '#ffffff'),
		foregroundColor: dark ? '#262626' : '#e5e5e5'
	}
}

/**
 * Widget overrides.
 *
 * Deliberately minimal: a transparent pane background so the chart inherits
 * whatever surface it is dropped onto, faint grid lines, and wicks the same
 * colour as their bodies (two-tone wicks read as noise at these densities).
 */
export function chartOverrides(
	dark: boolean,
	from?: Element | null
): Record<string, string | number | boolean> {
	const up = tokenColor('--success-ink', '#12B76A', from)
	const down = tokenColor('--destructive-ink', '#ef4444', from)
	// The site accent, used for the line/area series and the crosshair label so
	// a chart in line mode reads as Ordinals Wallet rather than as the
	// library's default blue.
	const accent = tokenColor('--primary-500', '#9E77ED', from)
	// The surface the chart is sitting on, not a guess at it. /alkanes paints
	// #060510 — near-black with a purple cast — and hardcoding #0a0a0a put a
	// flat black rectangle in the middle of it.
	const bg = surfaceColor(from, dark ? '#0a0a0a' : '#ffffff')
	const grid = dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)'
	const text = dark ? '#a3a3a3' : '#525252'

	return {
		'paneProperties.background': bg,
		'paneProperties.backgroundType': 'solid',
		'paneProperties.vertGridProperties.color': grid,
		'paneProperties.horzGridProperties.color': grid,
		'paneProperties.crossHairProperties.color': dark ? '#525252' : '#a3a3a3',
		'paneProperties.legendProperties.showVolume': true,

		'scalesProperties.textColor': text,
		'scalesProperties.lineColor': grid,
		'scalesProperties.fontSize': 11,

		'mainSeriesProperties.candleStyle.upColor': up,
		'mainSeriesProperties.candleStyle.downColor': down,
		'mainSeriesProperties.candleStyle.borderUpColor': up,
		'mainSeriesProperties.candleStyle.borderDownColor': down,
		'mainSeriesProperties.candleStyle.wickUpColor': up,
		'mainSeriesProperties.candleStyle.wickDownColor': down,

		'mainSeriesProperties.hollowCandleStyle.upColor': up,
		'mainSeriesProperties.hollowCandleStyle.downColor': down,
		'mainSeriesProperties.hollowCandleStyle.borderUpColor': up,
		'mainSeriesProperties.hollowCandleStyle.borderDownColor': down,

		// A line has no up or down — it is one continuous series — so painting it
		// green would assert a direction the series does not carry. The accent
		// is the honest choice.
		'mainSeriesProperties.areaStyle.lineColor': accent,
		'mainSeriesProperties.areaStyle.topColor': 'rgba(158,119,237,0.28)',
		'mainSeriesProperties.areaStyle.bottomColor': 'rgba(158,119,237,0.00)',
		'mainSeriesProperties.lineStyle.color': accent,
		'mainSeriesProperties.baselineStyle.topLineColor': up,
		'mainSeriesProperties.baselineStyle.bottomLineColor': down
	}
}
